import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import {
  Box,
  HStack,
  Icongraphy,
  NewFlag,
  Text,
  VStack,
} from '@design-system/pc';

import { theme } from '@/styles';
import { SearchShop } from '@remodeling/shop';

import { mapType } from '../../ShopSearch';
import LoadImage from '@/components/LoadImage';

interface CardProps {
  item: SearchShop;
  hoverItem: number | null;
  type: mapType;
  setHoverItem: (num: number | null) => void;
  isFirstItem: boolean;
}

const Card = ({
  item,
  hoverItem,
  setHoverItem,
  type,
  isFirstItem,
  ...props
}: CardProps) => {
  return (
    <Link href={`/shop/${item.id}`} passHref>
      <Box>
        <Box
          width="390px"
          ml={32}
          mt={isFirstItem ? 11 : 0}
          borderBottom="1px solid"
          borderColor="color_chip.gray.100"
        />
        <CardContainer
          backgroundColor={
            item.id === hoverItem ? 'color_chip.gray.50' : 'transparent'
          }
          onMouseOver={() => setHoverItem(item.id)}
          onMouseLeave={() => setHoverItem(null)}
          {...props}
        >
          <ImageWrapper>
            <LoadImage
              src={item.image.url}
              alt="storeImage"
              w={72}
              h={72}
              c={true}
            />
            {item.type.flagshipYn === 'Y' && (
              <StarIconBox>
                <Icongraphy
                  variant="ic_sparkle_solid"
                  size={14}
                  resize
                  fillColor={theme.colors.accent_colors.primary.default}
                />
              </StarIconBox>
            )}
          </ImageWrapper>
          <VStack space={4} alignment="start" width="calc(100% - 72px)">
            <Text
              variant="headline5"
              color={
                item.id === hoverItem
                  ? 'color_chip.blue.500'
                  : 'color_chip.primary'
              }
            >
              {item.type.dpName} {item.dpName}
            </Text>
            <StoreInfo>
              <IconBox>
                <Icongraphy
                  style={{ position: 'relative', top: '2px', left: '2px' }}
                  variant="ic_location"
                  size={14}
                  resize
                  fillColor={theme.colors.neutral_colors.gray[700]}
                />
              </IconBox>
              <Text variant="body2" color="color_chip.gray.600">
                {item.address.basic}
                {item.address.detail}
              </Text>
            </StoreInfo>
            {item.telNumber.telYn === 'Y' && (
              <StoreInfo>
                <IconBox>
                  <Icongraphy
                    style={{ position: 'relative', top: '2px', left: '2px' }}
                    variant="ic_call"
                    size={14}
                    resize
                    fillColor={theme.colors.neutral_colors.gray[700]}
                  />
                </IconBox>
                <Text variant="body2" color="color_chip.gray.600">
                  {item.telNumber.number}
                </Text>
              </StoreInfo>
            )}
            <StoreInfo>
              <IconBox>
                <Icongraphy
                  style={{ position: 'relative', top: '2px', left: '2px' }}
                  variant="ic_subject"
                  size={14}
                  resize
                  fillColor={theme.colors.neutral_colors.gray[700]}
                />
              </IconBox>
              <Text variant="body2" color="color_chip.gray.600">
                {item.includeItems.map((value, index) =>
                  index !== 0 ? `, ${value.name}` : value.name,
                )}
              </Text>
            </StoreInfo>
            {item.badges && type === 'remodeling' && (
              <HStack space={4}>
                {item.badges?.map((value, index) => (
                  <NewFlag
                    key={`${item.id}-${index}`}
                    variant="solid"
                    color="color_chip.blue.50"
                    textColor="color_chip.blue.500"
                    content={value.text}
                  />
                ))}
              </HStack>
            )}
          </VStack>
        </CardContainer>
      </Box>
    </Link>
  );
};

export default Card;

const CardContainer = styled(Box)`
  width: 100%;
  display: flex;
  gap: 16px;
  padding: 24px 28px;
  cursor: pointer;
`;

const IconBox = styled(Box)`
  min-width: 16px;
  min-height: 16px;
`;

const StoreInfo = styled(Box)`
  display: flex;
  flex: 1;
  gap: 4px;
`;

const ImageWrapper = styled(Box)`
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
`;

const StarIconBox = styled(Box)`
  position: absolute;
  top: 4px;
  left: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: white;
  border-radius: 100%;
  border: 0.5px solid rgba(0, 0, 0, 0.2);
  z-index: 1;
`;
